import { Component } from '@theme/component';
import { onDocumentLoaded, onDocumentReady, changeMetaThemeColor, setHeaderMenuStyle, updateAnnouncementBarHeight } from '@theme/utilities';

/**
 * @typedef {Object} HeaderComponentRefs
 * @property {HTMLDivElement} headerDrawerContainer - The header drawer container element
 * @property {HTMLElement} headerMenu - The header menu element
 * @property {HTMLElement} headerRowTop - The header top row element
 */

/**
 * @typedef {CustomEvent<{ minimumReached: boolean }>} OverflowMinimumEvent
 */

/**
 * A custom element that manages the site header.
 *
 * @extends {Component<HeaderComponentRefs>}
 */

class HeaderComponent extends Component {
  requiredRefs = ['headerDrawerContainer', 'headerMenu', 'headerRowTop'];

  /**
   * Width of window when header drawer was hidden
   * @type {number | null}
   */
  #menuDrawerHiddenWidth = null;

  /**
   * An intersection observer for monitoring sticky header position
   * @type {IntersectionObserver | null}
   */
  #intersectionObserver = null;

  /**
   * Whether the header has been scrolled offscreen, when sticky behavior is 'scroll-up'
   * @type {boolean}
   */
  #offscreen = false;

  /**
   * The last recorded scrollTop of the document, when sticky behavior is 'scroll-up
   * @type {number}
   */
  #lastScrollTop = 0;

  /**
   * A timeout to allow for hiding animation, when sticky behavior is 'scroll-up'
   * @type {number | null}
   */
  #timeout = null;

  /**
   * RAF ID for scroll handler throttling
   * @type {number | null}
   */
  #scrollRafId = null;

  /**
   * Keeps the global `--header-height` custom property up to date,
   * which other theme components can then consume
   */
  #resizeObserver = new ResizeObserver(([entry]) => {
    if (!entry || !entry.borderBoxSize[0]) return;

    // The initial height is calculated using the .offsetHeight property, which returns an integer.
    // We round to the nearest integer to avoid unnecessaary reflows.
    const roundedHeaderHeight = Math.round(entry.borderBoxSize[0].blockSize);
    document.body.style.setProperty('--header-height', `${roundedHeaderHeight}px`);

    // Check if the menu drawer should be hidden in favor of the header menu
    if (this.#menuDrawerHiddenWidth && window.innerWidth > this.#menuDrawerHiddenWidth) {
      this.#updateMenuVisibility(false);
    }
  });

  /**
   * Observes the header while scrolling the viewport to track when its actively sticky
   * @param {Boolean} alwaysSticky - Determines if we need to observe when the header is offscreen
   */
  #observeStickyPosition = (alwaysSticky = true) => {
    if (this.#intersectionObserver) return;

    const config = {
      threshold: alwaysSticky ? 1 : 0,
    };

    this.#intersectionObserver = new IntersectionObserver(([entry]) => {
      if (!entry) return;

      const { isIntersecting } = entry;

      if (alwaysSticky) {
        this.dataset.stickyState = isIntersecting ? 'inactive' : 'active';
        if (this.dataset.themeColor) changeMetaThemeColor(this.dataset.themeColor);
      } else {
        this.#offscreen = !isIntersecting || this.dataset.stickyState === 'active';
      }
    }, config);

    this.#intersectionObserver.observe(this);
  };

  /**
   * Handles the overflow minimum event from the header menu
   * @param {OverflowMinimumEvent} event
   */
  #handleOverflowMinimum = (event) => {
    this.#updateMenuVisibility(event.detail.minimumReached);
  };

  /**
   * Updates the visibility of the menu and drawer
   * @param {boolean} hideMenu - Whether to hide the menu and show the drawer
   */
  #updateMenuVisibility(hideMenu) {
    if (hideMenu) {
      this.#menuDrawerHiddenWidth = window.innerWidth;
    } else {
      this.#menuDrawerHiddenWidth = null;
    }
    setHeaderMenuStyle();
  }

  #handleWindowScroll = () => {
    if (this.#scrollRafId !== null) return;

    this.#scrollRafId = requestAnimationFrame(() => {
      this.#scrollRafId = null;
      this.#updateScrollState();
    });
  };

  #updateScrollState = () => {
    const stickyMode = this.getAttribute('sticky');
    if (!this.#offscreen && stickyMode !== 'always') return;

    const scrollTop = document.scrollingElement?.scrollTop ?? 0;
    const headerTop = this.getBoundingClientRect().top;
    const isScrollingUp = scrollTop < this.#lastScrollTop;
    const isAtTop = headerTop >= 0;

    if (this.#timeout) {
      clearTimeout(this.#timeout);
      this.#timeout = null;
    }

    if (stickyMode === 'always') {
      if (isAtTop) {
        this.dataset.scrollDirection = 'none';
      } else if (isScrollingUp) {
        this.dataset.scrollDirection = 'up';
      } else {
        this.dataset.scrollDirection = 'down';
      }

      this.#lastScrollTop = scrollTop;
      return;
    }

    if (isScrollingUp) {
      if (isAtTop) {
        // reset sticky state when header is scrolled up to natural position
        this.#offscreen = false;
        this.dataset.stickyState = 'inactive';
        this.dataset.scrollDirection = 'none';
      } else {
        // show sticky header when scrolling up
        this.dataset.stickyState = 'active';
        this.dataset.scrollDirection = 'up';
      }
    } else if (this.dataset.stickyState === 'active') {
      this.dataset.scrollDirection = 'none';

      this.dataset.stickyState = 'idle';
    } else {
      this.dataset.scrollDirection = 'none';
      this.dataset.stickyState = 'idle';
    }

    this.#lastScrollTop = scrollTop;
  };

  connectedCallback() {
    super.connectedCallback();
    this.#resizeObserver.observe(this);
    this.addEventListener('overflowMinimum', this.#handleOverflowMinimum);

    const stickyMode = this.getAttribute('sticky');
    if (stickyMode) {
      this.#observeStickyPosition(stickyMode === 'always');

      if (stickyMode === 'scroll-up' || stickyMode === 'always') {
        document.addEventListener('scroll', this.#handleWindowScroll);
      }
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#resizeObserver.disconnect();
    this.#intersectionObserver?.disconnect();
    this.removeEventListener('overflowMinimum', this.#handleOverflowMinimum);
    document.removeEventListener('scroll', this.#handleWindowScroll);
    if (this.#scrollRafId !== null) {
      cancelAnimationFrame(this.#scrollRafId);
      this.#scrollRafId = null;
    }
    document.body.style.setProperty('--header-height', '0px');
  }
}

if (!customElements.get('header-component')) {
  customElements.define('header-component', HeaderComponent);
}

onDocumentReady(() => {
  const headerGroup = document.querySelector('#header-group');

  // Note: Initial header heights are set via inline script in theme.liquid
  // This ResizeObserver handles dynamic updates after page load

  // Keep all current sizes: ResizeObserver delivers changed targets, not the whole group.
  if (headerGroup) {
    /** @type {Map<HTMLElement, number>} */
    const sizes = new Map();
    /** @type {Set<HTMLElement>} */
    const observedElements = new Set();

    /** @param {ResizeObserverEntry[]} [entries] */
    const syncObservedElements = (entries = []) => {
      if (document.querySelector('#header-group') !== headerGroup) {
        for (const element of observedElements) resizeObserver.unobserve(element);
        observedElements.clear();
        sizes.clear();
        return null;
      }
      const currentHeader = document.querySelector('header-component');
      /** @type {Set<HTMLElement>} */
      const elements = new Set();
      if (currentHeader instanceof HTMLElement) elements.add(currentHeader);
      for (const element of headerGroup.children) {
        if (element instanceof HTMLElement) elements.add(element);
      }

      for (const element of observedElements) {
        if (!elements.has(element)) {
          resizeObserver.unobserve(element);
          observedElements.delete(element);
          sizes.delete(element);
        }
      }
      for (const element of elements) {
        if (!observedElements.has(element)) {
          observedElements.add(element);
          // Prefer a delivered box; otherwise seed only rounded-zero discoveries.
          const reported = entries.find((entry) => entry.target === element)?.borderBoxSize?.[0]?.blockSize;
          if (typeof reported === 'number' && Number.isFinite(reported) && reported >= 0) {
            sizes.set(element, reported);
          } else if (element.offsetHeight === 0) {
            // An actual subpixel size can round to zero; its next RO entry wins.
            sizes.set(element, 0);
          }
          resizeObserver.observe(element, { box: 'border-box' });
        }
      }
      return currentHeader;
    };

    /** @param {Element | null} currentHeader */
    const updateHeaderGroupHeight = (currentHeader) => {
      if (document.querySelector('#header-group') !== headerGroup) return;
      let headerGroupHeight = 0;
      for (const element of headerGroup.children) {
        if (element === currentHeader || !(element instanceof HTMLElement)) continue;
        const height = sizes.get(element);
        // Leave the inline initial height in place until all contributors have a size.
        if (height === undefined) return;
        headerGroupHeight += height;
      }
      if (
        currentHeader instanceof HTMLElement &&
        currentHeader.hasAttribute('transparent') &&
        currentHeader.parentElement?.nextElementSibling
      ) {
        const height = sizes.get(currentHeader);
        if (height === undefined) return;
        headerGroupHeight += height;
      }

      // Preserve the existing observer's sum-then-round policy.
      const value = `${Math.round(headerGroupHeight)}px`;
      if (document.body.style.getPropertyValue('--header-group-height') !== value) {
        document.body.style.setProperty('--header-group-height', value);
      }
    };

    const resizeObserver = new ResizeObserver((entries) => {
      const currentHeader = syncObservedElements(entries);
      for (const entry of entries) {
        if (!(entry.target instanceof HTMLElement) || !observedElements.has(entry.target)) continue;
        const height = entry.borderBoxSize?.[0]?.blockSize;
        if (typeof height === 'number' && Number.isFinite(height) && height >= 0) {
          sizes.set(entry.target, height);
        }
      }
      updateHeaderGroupHeight(currentHeader);
    });

    syncObservedElements();

    const mutationObserver = new MutationObserver((mutations) => {
      const currentHeader = document.querySelector('header-component');
      const containsHeader = (node) =>
        node instanceof HTMLElement &&
        (node.tagName === 'HEADER-COMPONENT' || node.querySelector('header-component'));
      const affectsGroup = mutations.some(
        (mutation) =>
          mutation.type === 'childList' &&
          (mutation.target === headerGroup ||
            mutation.target === currentHeader?.parentElement?.parentElement ||
            [...mutation.addedNodes, ...mutation.removedNodes].some(containsHeader))
      );
      if (!affectsGroup) return;
      const observedHeader = syncObservedElements();
      updateHeaderGroupHeight(observedHeader);
    });

    // Also detect a header-component replacement inside an unchanged section wrapper.
    mutationObserver.observe(headerGroup, { childList: true, subtree: true });
  }

});

onDocumentLoaded(() => {
  const announcementBar = document.querySelector('#header-group .announcement-bar');
  if (announcementBar instanceof HTMLElement) {
    updateAnnouncementBarHeight();

    const announcementBarObserver = new ResizeObserver(() => {
      updateAnnouncementBarHeight();
    });

    announcementBarObserver.observe(announcementBar);
  } else {
    document.body.style.setProperty('--announcement-bar-height', '0px');
  }
});
